// The charts (/charts): seeds ranked by what people did with them in the last
// 7 days, and a producer leaderboard over 7 days / 30 days / all time. Every
// window is rolling — nothing resets, so the page is never empty on a Monday.
// The points shown are the XP rules (shared/badges.ts), listed in "How points
// work" from the same table the server scores with.
// Desktop: podium top-3 cards + ranked rows + sidebar (leaderboard, scoring).
// Mobile: swipeable top-3 strip, ranked rows, leaderboard.
//
// No genre filter: the catalogue is too small for one, which is why the pills
// are hidden on the feed too. When there are enough seeds to need them, they
// come back here and there together.

import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { GitBranch, Headphones, Heart } from "lucide-react";
import { CHART_WINDOWS, type ChartProducer, type ChartSeed, type ChartWindow, type Charts } from "@shared/api-types.js";
import { XP_RULES } from "@shared/badges.js";
import { Skeleton } from "@web-shared/components/ui/skeleton";
import { webApi } from "@web-shared/lib/web-api";
import { localRoomPath, profilePath, seedPath } from "@/lib/ctas";
import { cn } from "@web-shared/lib/utils";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { Avatar } from "../components/Avatar";
import { RemixByline } from "../components/RemixCredit";
import { SeedCover } from "../components/SeedCover";
import { AbletonInterstitial } from "../components/AbletonInterstitial";
import { AudiotoolInterstitial } from "../components/AudiotoolInterstitial";
import { DawPicker } from "../components/DawPicker";

import { EmptyState } from "../components/CardList";
import { FeedCardView, feedTrack } from "../components/FeedCardView";
import { usePlayer, useTrackStatus, type PlayerTrack } from "../player";
import { AmbientGlow } from "@/components/AmbientGlow";

/** The Remix menu's height — three rows and its padding. Used to decide
 * whether it opens below the button or above it. */
const MENU_H = 112;

const MEDAL_COLOR = ["text-[#e9c46a]", "text-[#c0c0c0]", "text-[#cd7f32]"] as const;
const MEDAL_BORDER = ["border-[#e9c46a]/40", "border-[#c0c0c0]/30", "border-[#cd7f32]/30"] as const;
const MEDAL_BG = ["#1c1a12", "#18181c", "#1a1610"] as const;

export function ChartsPage() {
  useDocumentTitle("Charts");
  const charts = useQuery({
    queryKey: ["web", "charts"],
    staleTime: 10 * 60_000,
    queryFn: () => webApi<Charts>("/charts"),
  });
  const queue = useMemo(
    () => (charts.data?.seeds ?? []).filter((c) => c.audioUrl !== "").map((c) => feedTrack(c)),
    [charts.data],
  );

  if (charts.isPending) return <Skeleton className="h-60 w-full rounded-2xl" />;
  if (charts.isError) return <EmptyState><p className="text-removed">Could not load the charts.</p></EmptyState>;

  const { seeds, producers } = charts.data;
  const podium = seeds.slice(0, 3);
  const rest = seeds.slice(3);

  return (
    <div>
      <AmbientGlow />

      {/* ---- Header ---- */}
      <header className="pt-4 sm:pt-8 pb-4 sm:pb-6">
        <div className="font-mono text-[11px] text-muted-foreground/50">
          Last 7 days · updated {new Date(charts.dataUpdatedAt).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
        </div>
        <h1 className="mt-1 text-4xl sm:text-5xl font-black tracking-tighter">Charts</h1>
        <p className="mt-1 text-sm text-muted-foreground hidden sm:block">
          The seeds people are flipping most, and who's making it happen.
        </p>
      </header>

      {/* ---- Desktop layout: grid ---- */}
      <div className="hidden sm:block">
        {/* The sidebar goes under the list below 1024px: beside it, the
            podium cards got too narrow for their own footers. */}
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_320px]">
          <main className="min-w-0 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold">Top seeds</h2>
              <span className="text-xs text-muted-foreground/50">Ranked by points from the last 7 days</span>
            </div>

            {/* Podium: top 3 as cards */}
            {podium.length > 0 && (
              <div className="grid grid-cols-3 gap-4">
                {podium.map((card, i) => (
                  <PodiumCard key={card.id} card={card} rank={i + 1} queue={queue} />
                ))}
              </div>
            )}

            {/* Remaining seeds as full feed cards */}
            {rest.length > 0 && (
              <div className="space-y-3">
                {rest.map((card, i) => (
                  <FeedCardView key={card.id} card={card} queue={queue} trending={i + 4} narrowWaveform />
                ))}
              </div>
            )}

            {seeds.length === 0 && <EmptyState><p>{NO_SEEDS}</p></EmptyState>}
          </main>

          {/* Sidebar — pt-10 aligns with the podium cards below the heading */}
          <aside className="flex flex-col gap-5 pt-10">
            <section className="rounded-2xl bg-raised border border-hairline p-5">
              <Leaderboard producers={producers} />
            </section>

            {/* How the chart works — collapsible */}
            <HowItWorks />
          </aside>
        </div>
      </div>

      {/* ---- Mobile layout: stacked ---- */}
      <div className="sm:hidden space-y-5 pb-20">
        {/* Top 3: swipeable strip */}
        {podium.length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-bold">Top seeds</h2>
            </div>
            <div className="-mx-4 px-4 overflow-x-auto scrollbar-none">
              <div className="flex gap-3 w-max">
                {podium.map((card, i) => (
                  <MobilePodiumCard key={card.id} card={card} rank={i + 1} queue={queue} />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Rest as ranked rows */}
        {rest.length > 0 && (
          <section>
            {rest.map((card, i) => (
              <MobileChartRow key={card.id} card={card} rank={i + 4} queue={queue} />
            ))}
          </section>
        )}

        {seeds.length === 0 && <EmptyState><p>{NO_SEEDS}</p></EmptyState>}

        <section>
          <Leaderboard producers={producers} />
        </section>
      </div>
    </div>
  );
}

/** Desktop podium: a tall card with rank, vinyl, title (2 lines max), icon stats, remix dropdown. */
function PodiumCard({ card, rank, queue }: { card: ChartSeed; rank: number; queue: PlayerTrack[] }) {
  const track = useMemo(() => feedTrack(card), [card]);
  const status = useTrackStatus(track.id);
  const player = usePlayer();
  const [showAbleton, setShowAbleton] = useState(false);
  const [showAudiotool, setShowAudiotool] = useState(false);
  // The card clips its contents (`overflow-hidden`, for the vinyl's glow), so
  // a menu positioned inside it was cut off at the card's bottom edge —
  // every entry but a sliver of the first. It hangs off the viewport instead,
  // measured from the button, and closes if the page moves under it.
  const menuButton = useRef<HTMLButtonElement>(null);
  const [menuAt, setMenuAt] = useState<{ top?: number; bottom?: number; right: number } | null>(null);
  const dropdownOpen = menuAt !== null;
  const openMenu = () => {
    const r = menuButton.current?.getBoundingClientRect();
    if (!r) return setMenuAt(null);
    const right = window.innerWidth - r.right;
    // Below the button, unless the window is too short for it there — the
    // podium sits low on the page and a menu that runs off the bottom is no
    // better than one the card cut off.
    setMenuAt(
      r.bottom + MENU_H + 8 <= window.innerHeight
        ? { top: r.bottom + 4, right }
        : { bottom: window.innerHeight - r.top + 4, right },
    );
  };
  useEffect(() => {
    if (!dropdownOpen) return;
    const close = () => setMenuAt(null);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [dropdownOpen]);
  const idx = rank - 1;

  return (
    <article
      className={cn(
        "group relative rounded-2xl border p-5 flex flex-col overflow-hidden",
        MEDAL_BORDER[idx] ?? "border-hairline",
      )}
      style={{ background: `linear-gradient(160deg, ${MEDAL_BG[idx] ?? "#141419"} 0%, #121217 65%)` }}
    >
      <div className="flex items-start justify-between">
        <span className={cn("text-4xl font-black", MEDAL_COLOR[idx] ?? "text-foreground/80")}>{rank}</span>
      </div>

      <div className="flex justify-center my-4">
        <SeedCover
          seed={{ id: card.id, title: card.title, genre: card.genre, bpm: card.bpm, key: card.key, producer: card.producer.username }}
          spinning={status === "playing"}
          interactive
          playStyle="overlay"
          playing={status === "playing"}
          remixBadge={card.remixOf ? "pill" : undefined}
          onPlay={() => player.toggle(track, queue)}
          className="w-[128px]"
        />
      </div>

      <Link to={seedPath(card.id)} className="font-bold text-lg leading-tight line-clamp-2 hover:text-primary">{card.title}</Link>
      <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
        <Avatar name={card.producer.username} url={card.producer.avatarUrl} className="size-5 text-[8px] !bg-white/10 !text-muted-foreground !ring-0" />
        {card.producer.username}
      </div>
      {/* One line fits on a podium card, so it names the person, not the title. */}
      {card.remixOf && <RemixByline origin={card.remixOf} variant="producer-only" className="mt-1 text-[11px]" />}

      <div className="mt-auto pt-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3 text-xs text-muted-foreground/50">
          <WeekStats card={card} />
        </div>
        <div>
          <button
            ref={menuButton}
            type="button"
            onClick={() => (dropdownOpen ? setMenuAt(null) : openMenu())}
            className="h-7 px-2.5 rounded-md bg-primary text-primary-foreground text-[11px] font-bold flex items-center gap-1"
          >
            Remix
            <svg className={cn("size-3 transition-transform", dropdownOpen && "rotate-180")} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" viewBox="0 0 24 24"><path d="M6 9l6 6 6-6" /></svg>
          </button>
          {menuAt && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setMenuAt(null)} />
              <div
                className="fixed z-50 w-40 rounded-xl border border-white/10 bg-[#17171d] py-1 shadow-[0_8px_24px_rgba(0,0,0,0.5)]"
                style={{ top: menuAt.top, bottom: menuAt.bottom, right: menuAt.right }}
              >
                <button type="button" onClick={() => { setMenuAt(null); setShowAbleton(true); }} className="flex w-full items-center gap-2 px-3 py-2 text-xs font-semibold hover:bg-white/5 text-left">Ableton</button>
                <button type="button" onClick={() => { setMenuAt(null); setShowAudiotool(true); }} className="flex w-full items-center gap-2 px-3 py-2 text-xs font-semibold hover:bg-white/5 text-left">Audiotool</button>
                <Link to={localRoomPath(card.id)} className="flex items-center gap-2 px-3 py-2 text-xs font-semibold hover:bg-white/5" onClick={() => setMenuAt(null)}>Browser</Link>
              </div>
            </>
          )}
        </div>
      </div>
      {showAbleton && (
        <AbletonInterstitial
          seedId={card.id}
          seedTitle={card.title}
          producer={card.producer.username}
          trackCount={card.stemCount}
          onClose={() => setShowAbleton(false)}
        />
      )}
      {showAudiotool && (
        <AudiotoolInterstitial
          seedId={card.id}
          seedTitle={card.title}
          producer={card.producer.username}
          trackCount={card.stemCount}
          onClose={() => setShowAudiotool(false)}
        />
      )}
    </article>
  );
}

/** Mobile podium: compact swipeable card with medal color, disc, stats, remix. */
function MobilePodiumCard({ card, rank, queue }: { card: ChartSeed; rank: number; queue: PlayerTrack[] }) {
  const track = useMemo(() => feedTrack(card), [card]);
  const status = useTrackStatus(track.id);
  const player = usePlayer();
  const [showPicker, setShowPicker] = useState(false);
  const idx = rank - 1;

  return (
    <>
      <div
        className={cn(
          "shrink-0 w-[150px] rounded-2xl border bg-raised p-3 flex flex-col",
          MEDAL_BORDER[idx] ?? "border-hairline",
        )}
      >
        <div className={cn("text-2xl font-black", MEDAL_COLOR[idx] ?? "text-foreground/80")}>{rank}</div>
        <Link to={seedPath(card.id)} className="flex justify-center my-2">
          <SeedCover
            seed={{ id: card.id, title: card.title, genre: card.genre, bpm: card.bpm, key: card.key, producer: card.producer.username }}
            spinning={status === "playing"}
            interactive
            playStyle="overlay"
            playing={status === "playing"}
            remixBadge={card.remixOf ? "pill-sm" : undefined}
            onPlay={() => player.toggle(track, queue)}
            className="w-[90px]"
          />
        </Link>
        <Link to={seedPath(card.id)} className="font-bold text-sm truncate">{card.title}</Link>
        <div className="text-[11px] text-muted-foreground/60">{card.producer.username}</div>
        {card.remixOf && <RemixByline origin={card.remixOf} variant="producer-only" className="text-[10px]" />}
        <div className="flex items-center gap-2 font-mono text-[10px] text-muted-foreground/50 mt-1">
          <WeekStats card={card} small />
        </div>
        <button
          type="button"
          onClick={() => setShowPicker(true)}
          className="mt-2 h-7 rounded-lg bg-primary text-primary-foreground text-[11px] font-bold"
        >
          Remix
        </button>
      </div>
      {showPicker && <DawPicker seedId={card.id} seedTitle={card.title} onClose={() => setShowPicker(false)} />}
    </>
  );
}

/** Mobile chart row: rank + disc + info + stats + remix button. */
function MobileChartRow({ card, rank, queue }: { card: ChartSeed; rank: number; queue: PlayerTrack[] }) {
  const track = useMemo(() => feedTrack(card), [card]);
  const status = useTrackStatus(track.id);
  const player = usePlayer();
  const [showPicker, setShowPicker] = useState(false);

  return (
    <>
      <article className="flex items-center gap-3 py-3 border-b border-white/5">
        <span className="w-4 font-black text-muted-foreground/50">{rank}</span>
        <SeedCover
          seed={{ id: card.id, title: card.title, genre: card.genre, bpm: card.bpm, key: card.key, producer: card.producer.username }}
          spinning={status === "playing"}
          interactive
          playStyle="overlay"
          playing={status === "playing"}
          remixBadge={card.remixOf ? "pip" : undefined}
          onPlay={() => player.toggle(track, queue)}
          className="w-12 shrink-0"
        />
        <Link to={seedPath(card.id)} className="flex-1 min-w-0">
          <div className="font-bold truncate">{card.title}</div>
          <div className="text-xs text-muted-foreground truncate">{card.producer.username}</div>
          {/* Unlinked: already inside the row's Link to the seed. */}
          {card.remixOf && (
            <RemixByline origin={card.remixOf} variant="producer-only" linked={false} className="text-[10px] text-primary/80" />
          )}
          <div className="flex items-center gap-2 font-mono text-[10px] text-muted-foreground/50 mt-0.5">
            <WeekStats card={card} small />
          </div>
        </Link>
        <button
          type="button"
          onClick={() => setShowPicker(true)}
          className="h-8 px-3 rounded-full bg-primary text-primary-foreground text-xs font-bold shrink-0"
        >
          Remix
        </button>
      </article>
      {showPicker && <DawPicker seedId={card.id} seedTitle={card.title} onClose={() => setShowPicker(false)} />}
    </>
  );
}

const NO_SEEDS = "Nothing has been remixed, liked or played in the last 7 days.";

const WINDOW_LABEL: Record<ChartWindow, string> = { "7d": "7 days", "30d": "30 days", all: "All time" };
const WINDOW_EMPTY: Record<ChartWindow, string> = {
  "7d": "No XP earned in the last 7 days yet.",
  "30d": "No XP earned in the last 30 days yet.",
  all: "No XP earned yet.",
};

/** What earned a seed its place: its points, then this week's remixes, likes
 * and listeners. */
function WeekStats({ card, small = false }: { card: ChartSeed; small?: boolean }) {
  const icon = small ? "size-2.5" : "size-3";
  const { points, remixes, likes, listeners } = card.chart;
  return (
    <>
      <span className="font-bold text-primary" title="Points from the last 7 days">+{points}</span>
      <span className="flex items-center gap-0.5" title="Remixes in the last 7 days"><GitBranch className={icon} /> {remixes}</span>
      <span className="flex items-center gap-0.5" title="Likes in the last 7 days"><Heart className={icon} /> {likes}</span>
      <span className="flex items-center gap-0.5" title="Listeners in the last 7 days"><Headphones className={icon} /> {listeners}</span>
    </>
  );
}

/** Top producers, one rolling window at a time. */
function Leaderboard({ producers }: { producers: Charts["producers"] }) {
  const [span, setSpan] = useState<ChartWindow>("7d");
  const rows = producers[span];
  return (
    <>
      <h3 className="font-bold">Top producers</h3>
      <div className="mt-3">
        <div role="tablist" aria-label="Leaderboard window" className="grid grid-cols-3 rounded-lg bg-white/5 p-0.5 text-xs font-semibold">
          {CHART_WINDOWS.map((w) => (
            <button
              key={w}
              type="button"
              role="tab"
              aria-selected={w === span}
              onClick={() => setSpan(w)}
              className={cn(
                "whitespace-nowrap rounded-md px-2 py-1.5",
                w === span ? "bg-white/10 text-foreground" : "text-muted-foreground/60 hover:text-foreground",
              )}
            >
              {WINDOW_LABEL[w]}
            </button>
          ))}
        </div>
      </div>
      <div className="mt-4 flex flex-col gap-1">
        {rows.map((p, i) => (
          <ProducerRow key={p.id} producer={p} rank={i + 1} total={span === "all"} />
        ))}
        {rows.length === 0 && <p className="py-2 text-sm text-muted-foreground">{WINDOW_EMPTY[span]}</p>}
      </div>
    </>
  );
}

/** Sidebar producer row. */
function ProducerRow({ producer, rank, total }: { producer: ChartProducer; rank: number; total: boolean }) {
  return (
    <Link
      to={profilePath(producer.handle)}
      className="flex items-center gap-3 p-2 -mx-2 rounded-xl hover:bg-white/5"
    >
      <span className={cn("w-5 text-center font-black", rank === 1 ? "text-[#e9c46a]" : "text-muted-foreground/50")}>{rank}</span>
      <Avatar name={producer.username} url={producer.avatarUrl} className="size-9" />
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-semibold">{producer.username}</span>
        <span className="block text-[11px] text-muted-foreground/50">@{producer.handle}</span>
      </span>
      {/* A window's gain reads +N; all time is the profile's total. */}
      <span className="font-mono text-sm text-primary">{total ? producer.xp.toLocaleString("en-US") : `+${producer.xp}`}</span>
    </Link>
  );
}

function HowItWorks() {
  const [open, setOpen] = useState(false);
  return (
    <section className="rounded-2xl border border-dashed border-white/10 p-5 text-sm">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between font-bold"
      >
        How points work
        <svg
          className={cn("size-4 text-muted-foreground transition-transform", open && "rotate-180")}
          fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" viewBox="0 0 24 24"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open && (
        <ul className="mt-3 flex flex-col gap-2 text-muted-foreground">
          {XP_RULES.map((rule) => (
            <li key={rule.label} className="flex justify-between gap-3">
              <span>{rule.label}</span>
              <span className="font-mono text-primary">+{rule.points}</span>
            </li>
          ))}
          <li className="text-[11px] text-muted-foreground/60">
            Seeds rank by the remix, like and listener points they earned in the last 7 days. Your own listens and likes
            don't count.
          </li>
        </ul>
      )}
    </section>
  );
}
